import { createContext, useContext, useState, useEffect, useRef } from "react";
import { auth } from "../backend/utils/init-firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import { trackingStore, userStore } from "../zustand/globalStore";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../backend/utils/init-firebase";
import {
  EMAILS_FOR_GRANTING_PREMIUM_PLAN,
  UserEmailAndNotificationPreferences,
  SubscriptionStatus,
  USERS_COLLECTION_NAME,
  UserData,
  UserSubscriptionPlan,
  JUNGLE_BACKEND_PROD_DOMAIN,
  CONSTRAINED_GENERATIONS_COUNTRY_CODES,
  getInitialMonthlyGenerationsLeft,
  JUNGLE_CLOUDFLARE_BASE_URL,
  NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import {
  checkAndUpdateUserGenerationLimits,
  getMonthlyGenerationResetDate,
  createUserOnFirebase,
  getLocationData,
  identifyUserForAnalytics,
  isFirebaseDate,
  LocationData,
  wasUserCreatedLessThanOneHourAgo,
} from "../backend/utils/helper-methods";
import { updateUser } from "../backend/models/UserModel";
import {
  migrateUserGamificationPropertiesIfNeeded,
  migrateMegaMindUsersToSuperLearner,
  migrateUserBadgesProgressAndStatsIfNeeded,
} from "../backend/utils/firebaseMigrationsAndFieldChanges";
import { getNumberOfSetsOfCardsGeneratedForUser } from "../backend/models/DeckModel";
import { identifyEluAnalyticsUser } from "../utils/analytics/elu-client";
import { getFirebaseAuthorizationHeaders } from "../backend/utils/firebase-auth-headers";
import { trackError } from "../utils/analytics/track-error";
import { markStartupPhase } from "../utils/startup-profile";
import {
  markFirebaseSessionAsExpected,
  reportUnexpectedFirebaseSessionLoss,
} from "../backend/utils/auth-session-observability";
import { signOutWebViewAfterMobileAuthenticationEnded } from "../backend/utils/mobile-authentication";
import { toSignedInFirebaseUser } from "../backend/utils/anonymous-read-session";
import { tutorVoiceLogoutCleanup } from "../features/tutor/client/tutorVoiceLogout";
import { clearAcquisitionTouchesForSignedInAccount } from "../utils/attribution-module-loaders";
import {
  FREE_ALLOWANCE_TEST,
  FirstExperienceTestAssignment,
  resolveFirstExperienceTestAssignment,
} from "../first-experience-tests/firstExperienceTests";
import { trackFirstExperienceTestExposure } from "../first-experience-tests/trackFirstExperienceTestExposure";
import {
  claimPendingMobileAuthenticationBootstrapTimeout,
  getMobileAuthenticationBootstrapState,
  isMobileAuthenticationBootstrapPending,
  subscribeToMobileAuthenticationBootstrap,
} from "../backend/utils/mobile-authentication-bootstrap";

interface AuthContextProps {
  currentUser: null | {};
  authenticationLoading: boolean;
  accountSetupError: boolean;
  accountLoadError: boolean;
}

const PROFILE_CREATION_MAX_ATTEMPTS = 3;
const PROFILE_CREATION_RETRY_DELAY_MS = 500;
const MOBILE_AUTHENTICATION_BOOTSTRAP_TIMEOUT_MS = 10000;

const wait = (durationInMilliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, durationInMilliseconds));

const AuthContext = createContext<AuthContextProps | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Firestore rules accept only sign-ins completed after the 2026-11-01 incident
// containment cutoff. An older session can never load the account, so return it
// to sign-in instead of leaving the learner on the unavailable screen.
const INCIDENT_SESSION_CUTOFF_EPOCH_SECONDS = 1793507933;

async function signOutSessionStartedBeforeIncidentCutoff(user: User) {
  try {
    const { claims } = await user.getIdTokenResult();
    if (Number(claims.auth_time) > INCIDENT_SESSION_CUTOFF_EPOCH_SECONDS) return;
    await signOutWebViewAfterMobileAuthenticationEnded();
  } catch (error) {
    trackError(error, { scope: "incident-session-cutoff-sign-out", userId: user.uid });
  }
}

export function AuthProvider({ children }) {
  const [userFirebaseAuth, setUserFirebaseAuth] = useState(null);
  const hasFetchedNumberOfSetsRef = useRef(false);
  // Tracks the last Firebase Auth email we already pushed to Firestore so the change-email
  // sync only runs once per actual email change (the snapshot listener can fire many times).
  const lastSyncedEmailForChangeEmailFlowRef = useRef<string | null>(null);
  const profileCreationInFlightRef = useRef<Promise<void> | null>(null);
  const profileCreationFailedRef = useRef(false);
  const hasLoadedAuthenticatedUserProfileRef = useRef(false);
  const mobileAuthenticationBootstrapTimeoutRef = useRef<number | null>(null);

  const {
    userReferrerId,
    didUserComeFromMetaAd,
    googleCampaignId,
    tiktokCampaignId,
    tiktokClickId,
    redditCampaignId,
    influencerCampaignId,
    newsletterCampaignId,
    idOfAmbassadorThatReferredUser,
  } = trackingStore((state) => state);

  const { setCurrentUser: setCurrentUserInStore } = userStore();

  const [currentUser, setCurrentUser] = useState<null | {}>(null);
  const [authenticationLoading, setAuthenticationLoading] = useState(true);
  const [accountSetupError, setAccountSetupError] = useState(false);
  const [accountLoadError, setAccountLoadError] = useState(false);

  useEffect(() => {
    const clearMobileAuthenticationBootstrapTimeout = () => {
      if (mobileAuthenticationBootstrapTimeoutRef.current === null) return;
      window.clearTimeout(mobileAuthenticationBootstrapTimeoutRef.current);
      mobileAuthenticationBootstrapTimeoutRef.current = null;
    };
    const startMobileAuthenticationBootstrapTimeout = () => {
      if (mobileAuthenticationBootstrapTimeoutRef.current !== null) return;

      mobileAuthenticationBootstrapTimeoutRef.current = window.setTimeout(async () => {
        mobileAuthenticationBootstrapTimeoutRef.current = null;
        if (!claimPendingMobileAuthenticationBootstrapTimeout()) return;

        trackError(new Error("Native authentication bootstrap timed out"), {
          scope: "mobile-authentication-bootstrap-timeout",
          level: "warning",
          userId: auth.currentUser?.uid,
          tags: {
            feature: "mobile_authentication",
          },
        });

        try {
          await signOutWebViewAfterMobileAuthenticationEnded();
        } catch (error) {
          trackError(error, {
            scope: "mobile-authentication-bootstrap-timeout-sign-out",
            level: "error",
            userId: auth.currentUser?.uid,
            tags: {
              feature: "mobile_authentication",
            },
          });
        }
      }, MOBILE_AUTHENTICATION_BOOTSTRAP_TIMEOUT_MS);
    };
    const handleMobileAuthenticationBootstrapState = (
      mobileAuthenticationBootstrapState: ReturnType<typeof getMobileAuthenticationBootstrapState>
    ) => {
      if (mobileAuthenticationBootstrapState === "pending") {
        setAuthenticationLoading(true);
        clearMobileAuthenticationBootstrapTimeout();
        startMobileAuthenticationBootstrapTimeout();
        return;
      }

      clearMobileAuthenticationBootstrapTimeout();
      if (mobileAuthenticationBootstrapState === "signed_in") {
        if (hasLoadedAuthenticatedUserProfileRef.current && auth.currentUser) {
          setAuthenticationLoading(false);
        }
        return;
      }

      if (mobileAuthenticationBootstrapState === "signed_out") {
        hasLoadedAuthenticatedUserProfileRef.current = false;
        setAccountLoadError(false);
        setUserFirebaseAuth(null);
        setCurrentUserInStore(null);
        setCurrentUser(null);
        setAuthenticationLoading(false);
      }
    };

    const initialMobileAuthenticationBootstrapState = getMobileAuthenticationBootstrapState();
    handleMobileAuthenticationBootstrapState(initialMobileAuthenticationBootstrapState);
    const unsubscribeFromMobileAuthenticationBootstrap = subscribeToMobileAuthenticationBootstrap(
      handleMobileAuthenticationBootstrapState
    );

    return () => {
      clearMobileAuthenticationBootstrapTimeout();
      unsubscribeFromMobileAuthenticationBootstrap();
    };
  }, [setCurrentUserInStore]);

  useEffect(() => {
    let previousIdentity = toSignedInFirebaseUser(auth.currentUser);
    const observeVoiceIdentity = (user: User | null, nativeBootstrapPending = false) => {
      // A native token handoff can temporarily remove the Firebase identity.
      // Keep its journal until the native bridge confirms logout or a different
      // owner signs in; a same-owner handoff is still the current login.
      if (nativeBootstrapPending && (!user || user.uid === previousIdentity?.uid)) {
        if (user) previousIdentity = user;
        return;
      }
      if (previousIdentity && previousIdentity !== user) {
        void tutorVoiceLogoutCleanup.queue(previousIdentity.uid).catch(error => {
          trackError(error, { scope: "tutor-voice-journal-logout" });
        });
      }
      previousIdentity = user;
    };
    const unsubscribeVoiceBootstrap = subscribeToMobileAuthenticationBootstrap(state => {
      if (state === "signed_out") observeVoiceIdentity(null);
    });
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      markStartupPhase("firebase_auth_resolved");
      // An anonymous session only lets a signed-out visitor read the set they generated
      // (anonymous-read-session.ts); it is not an account, so the app stays signed out.
      const user = toSignedInFirebaseUser(firebaseUser);
      const isNativeAuthenticationBootstrapStillPending = isMobileAuthenticationBootstrapPending();
      observeVoiceIdentity(user, isNativeAuthenticationBootstrapStillPending);
      const shouldClearAuthenticatedUserState = !user && !isNativeAuthenticationBootstrapStillPending;

      if (user) {
        markFirebaseSessionAsExpected(user.uid);
      } else if (shouldClearAuthenticatedUserState) {
        reportUnexpectedFirebaseSessionLoss();
      }
      hasFetchedNumberOfSetsRef.current = false;
      profileCreationInFlightRef.current = null;
      profileCreationFailedRef.current = false;
      hasLoadedAuthenticatedUserProfileRef.current = false;
      setAccountSetupError(false);
      setAccountLoadError(false);
      setUserFirebaseAuth(user);
      if (shouldClearAuthenticatedUserState) {
        setCurrentUserInStore(null);
        setCurrentUser(null);
      }
      setAuthenticationLoading(Boolean(user) || isNativeAuthenticationBootstrapStillPending);
    });
    return () => { unsubscribe(); unsubscribeVoiceBootstrap(); };
  }, [setCurrentUserInStore]);

  useEffect(() => {
    if (!userFirebaseAuth) {
      return;
    }
    let isSnapshotListenerActive = true;
    let profileSnapshotRevision = 0;
    let generationCountRevision = 0;
    let latestGenerationCount: UserData["number_of_sets_of_cards_generated"];
    let haveAcquisitionTouchesBeenClearedForThisAccount = false;
    // A rejected reset write (server-owned counters, e.g. a Tutor-enrolled account) reverts the profile and
    // fires this listener again. Retrying on every snapshot looped the Firestore write stream — one new
    // WebChannel session per attempt, ~13/s — so each due reset date is attempted once per listener.
    let monthlyGenerationResetAttemptedForDateMs: number | undefined;
    const userDocRef = doc(db, USERS_COLLECTION_NAME, userFirebaseAuth.uid);
    let snapshotUserDataUpdate = onSnapshot(userDocRef, { includeMetadataChanges: true }, async (data) => {
      if (!isSnapshotListenerActive) {
        return;
      }

      if (!data.exists()) {
        if (profileCreationInFlightRef.current || profileCreationFailedRef.current) {
          return;
        }

        const profileCreationPromise = (async () => {
          const numOfAdvancedQuestionGenerationsAllowed = NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED;

          let latestProfileCreationError: unknown;
          for (let attemptNumber = 1; attemptNumber <= PROFILE_CREATION_MAX_ATTEMPTS; attemptNumber += 1) {
            if (!isSnapshotListenerActive) {
              return;
            }

            try {
              await createUserOnFirebase(
                userFirebaseAuth,
                userReferrerId,
                tiktokCampaignId,
                tiktokClickId,
                didUserComeFromMetaAd,
                googleCampaignId,
                redditCampaignId,
                influencerCampaignId,
                newsletterCampaignId,
                idOfAmbassadorThatReferredUser,
                numOfAdvancedQuestionGenerationsAllowed
              );
              return;
            } catch (error) {
              latestProfileCreationError = error;
              if (!isSnapshotListenerActive) {
                return;
              }
              if (attemptNumber < PROFILE_CREATION_MAX_ATTEMPTS) {
                await wait(PROFILE_CREATION_RETRY_DELAY_MS * attemptNumber);
              }
            }
          }

          if (!isSnapshotListenerActive) {
            return;
          }

          profileCreationFailedRef.current = true;
          setAccountSetupError(true);
          setAuthenticationLoading(false);
          trackError(latestProfileCreationError, {
            scope: "new-user-profile-bootstrap-exhausted",
            userId: userFirebaseAuth.uid,
            extra: { attempts: PROFILE_CREATION_MAX_ATTEMPTS },
          });
        })();
        profileCreationInFlightRef.current = profileCreationPromise;

        try {
          await profileCreationPromise;
        } finally {
          // An older auth session must not clear a newer session's in-flight profile creation.
          if (profileCreationInFlightRef.current === profileCreationPromise) {
            profileCreationInFlightRef.current = null;
          }
        }
        return;
      }
      let updatedUserObject = data.data() as UserData;
      updatedUserObject.id = userFirebaseAuth.uid;
      // Even a pending local count change supersedes an older repair. Unrelated
      // profile writes and cache-to-server metadata updates do not.
      if (updatedUserObject.number_of_sets_of_cards_generated !== latestGenerationCount) {
        latestGenerationCount = updatedUserObject.number_of_sets_of_cards_generated;
        generationCountRevision += 1;
      }
      if (data?.metadata?.hasPendingWrites) {
        return;
      }

      // Confirmed snapshots own profile state. Pending maintenance writes must
      // not cancel the confirmed profile that makes the home screen ready.
      const snapshotRevision = ++profileSnapshotRevision;
      markStartupPhase("profile_snapshot");

      // Account boundary for per-ad attribution: once this account's profile is confirmed, the campaign
      // touches this browser recorded belong to it (written on its user doc when it was created here, or no
      // acquisition at all for an existing account). Clearing them keeps a later sign-up on this browser,
      // after a sign-out, from inheriting this account's campaign.
      if (!haveAcquisitionTouchesBeenClearedForThisAccount) {
        haveAcquisitionTouchesBeenClearedForThisAccount = true;
        clearAcquisitionTouchesForSignedInAccount();
      }
      const isCurrentProfileSnapshot = () =>
        isSnapshotListenerActive &&
        auth.currentUser?.uid === userFirebaseAuth.uid &&
        snapshotRevision === profileSnapshotRevision;

      // After the user clicks the Firebase verifyBeforeUpdateEmail link, Firebase Auth's email is
      // updated server-side. We then sync Firestore (email_auth + email_communication) by calling
      // the existing /user/change_user_email endpoint. The endpoint is idempotent and is also used
      // by the admin "Change User Email" flow in AdminSearchUsersPage.
      const firebaseAuthEmail = userFirebaseAuth?.email;
      const firestoreEmail = updatedUserObject?.email_auth;
      if (
        firebaseAuthEmail &&
        firestoreEmail &&
        firebaseAuthEmail.toLowerCase() !== firestoreEmail.toLowerCase() &&
        lastSyncedEmailForChangeEmailFlowRef.current !== firebaseAuthEmail.toLowerCase()
      ) {
        const normalizedFirebaseAuthEmail = firebaseAuthEmail.toLowerCase();
        // Suppress duplicate Firestore snapshot retries while this request is
        // in flight, but clear the marker on failure so a later snapshot can
        // retry instead of leaving the two email fields stale.
        lastSyncedEmailForChangeEmailFlowRef.current = normalizedFirebaseAuthEmail;
        void getFirebaseAuthorizationHeaders()
          .then((firebaseAuthorizationHeaders) =>
            fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/change_user_email`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...firebaseAuthorizationHeaders,
              },
              body: JSON.stringify({
                user_id: updatedUserObject.id,
                new_user_email: firebaseAuthEmail,
              }),
            })
          )
          .then((changeUserEmailResponse) => {
            if (!changeUserEmailResponse.ok) {
              throw new Error(`Email sync failed with status ${changeUserEmailResponse.status}`);
            }
          })
          .catch((errorSyncingChangedEmailToFirestore) => {
            if (lastSyncedEmailForChangeEmailFlowRef.current === normalizedFirebaseAuthEmail) {
              lastSyncedEmailForChangeEmailFlowRef.current = null;
            }
            console.error(
              "failed to sync changed firebase auth email to firestore:",
              errorSyncingChangedEmailToFirestore
            );
          });
      }

      if (
        EMAILS_FOR_GRANTING_PREMIUM_PLAN.includes(userFirebaseAuth?.email?.toLowerCase()) &&
        updatedUserObject.subscription_information?.subscription_status !== SubscriptionStatus.ACTIVE
      ) {
        updateUser(userFirebaseAuth.uid, {
          subscription_information: {
            ...updatedUserObject.subscription_information,
            subscription_status: "active",
            subscription_plan_type: UserSubscriptionPlan.SUPER_LEARNER,
          },
        });
        updatedUserObject.subscription_information.subscription_status = SubscriptionStatus.ACTIVE;
        updatedUserObject.subscription_information.subscription_plan_type = UserSubscriptionPlan.SUPER_LEARNER;
      }

      if (
        updatedUserObject?.user_settings?.should_include_card_url_source_when_saving_to_anki === undefined ||
        updatedUserObject?.user_settings?.should_include_card_url_source_when_saving_to_anki === null
      ) {
        updatedUserObject.user_settings = {
          should_include_card_url_source_when_saving_to_anki: false,
          email_notification_preferences: [
            UserEmailAndNotificationPreferences.productUpdateEmails,
            UserEmailAndNotificationPreferences.reviewReminderEmails,
            UserEmailAndNotificationPreferences.weeklyReviewEmails,
          ],
          notification_preferences: {
            review_reminder_on_mobile: true,
            review_reminder_on_desktop: false,
          },
        };
      }

      //migrate communication settings
      if (!updatedUserObject?.user_settings?.email_notification_preferences) {
        switch (updatedUserObject?.email_communication_settings) {
          case UserEmailAndNotificationPreferences.allEmails:
            updatedUserObject.user_settings.email_notification_preferences = [
              UserEmailAndNotificationPreferences.productUpdateEmails,
              UserEmailAndNotificationPreferences.reviewReminderEmails,
              UserEmailAndNotificationPreferences.weeklyReviewEmails,
            ];
            break;
          case UserEmailAndNotificationPreferences.productUpdateEmails:
            updatedUserObject.user_settings.email_notification_preferences = [
              UserEmailAndNotificationPreferences.productUpdateEmails,
            ];
            break;
          case UserEmailAndNotificationPreferences.reviewReminderEmails:
            updatedUserObject.user_settings.email_notification_preferences = [
              UserEmailAndNotificationPreferences.reviewReminderEmails,
            ];
            break;
          case UserEmailAndNotificationPreferences.weeklyReviewEmails:
            updatedUserObject.user_settings.email_notification_preferences = [
              UserEmailAndNotificationPreferences.reviewReminderEmails,
            ];
            break;
          case UserEmailAndNotificationPreferences.noEmails:
            updatedUserObject.user_settings.email_notification_preferences = [];
            break;
          default:
            updatedUserObject.user_settings.email_notification_preferences = [
              UserEmailAndNotificationPreferences.productUpdateEmails,
              UserEmailAndNotificationPreferences.reviewReminderEmails,
              UserEmailAndNotificationPreferences.weeklyReviewEmails,
            ];
        }
        updateUser(userFirebaseAuth.uid, {
          user_settings: updatedUserObject.user_settings,
        });
      }

      if (!updatedUserObject?.country_code) {
        getLocationData()
          .then((locationData: LocationData) => {
            const updatesForUser: Partial<UserData> = {
              country_code: locationData?.country,
              ip_address: locationData?.ipAddress,
            };

            // Constrained-geography users get 1 generation/month instead of the default 2, so we
            // override their starting allowance here once we know their country. Only apply this
            // when the user hasn't generated anything yet — otherwise an in-flight backend
            // decrement could race with this write and get clobbered (which was leaving
            // num_of_monthly_generations_left at the full allowance after the first generation).
            const isUserInConstrainedGeography =
              !!locationData?.country &&
              CONSTRAINED_GENERATIONS_COUNTRY_CODES.includes(locationData.country.toUpperCase());
            const userHasNotGeneratedAnythingYet = !updatedUserObject?.number_of_sets_of_cards_generated;
            // The first-experience test T5b (freeAllowanceTest.ts): such a new account keeps everyone's two
            // free generations on one side. Its side is written on the account, so every device and every
            // monthly reset reads it (a side forced for QA is not written).
            let freeAllowanceAssignment: FirstExperienceTestAssignment | null = null;
            if (isUserInConstrainedGeography && userHasNotGeneratedAnythingYet) {
              freeAllowanceAssignment = resolveFirstExperienceTestAssignment(FREE_ALLOWANCE_TEST, updatedUserObject);
              if (freeAllowanceAssignment && !freeAllowanceAssignment.isForcedForQa) {
                updatesForUser.experiment_variants = {
                  ...(updatedUserObject.experiment_variants ?? {}),
                  [FREE_ALLOWANCE_TEST.experimentId]: freeAllowanceAssignment.variant,
                };
              }
            }
            // On the "two_generations" side the account keeps the two it was created with, so this block writes
            // nothing to the counter (and no write can race a generation's decrement). A side forced for QA
            // changes nothing here: the counter is lowered as for any account outside the test.
            const isOnTheTwoGenerationsSide =
              freeAllowanceAssignment?.variant === "two_generations" && !freeAllowanceAssignment.isForcedForQa;
            if (isUserInConstrainedGeography && userHasNotGeneratedAnythingYet && !isOnTheTwoGenerationsSide) {
              const haveCardsBeenGeneratedAnonymouslyBeforeSignup =
                typeof window !== "undefined" && !!localStorage.getItem("haveCardsBeenGeneratedBefore");
              const constrainedGeographyAllowance = getInitialMonthlyGenerationsLeft(
                locationData.country,
                haveCardsBeenGeneratedAnonymouslyBeforeSignup
              );
              // Only the counter (updateUser merges maps): this snapshot's other values can be stale by now
              updatesForUser.subscription_information = {
                num_of_monthly_generations_left: constrainedGeographyAllowance,
              } as UserData["subscription_information"];
            }

            updateUser(userFirebaseAuth.uid, updatesForUser);
            // Reported once the side is on the account: it changes the free generations the account sees. Not
            // identified from here: this listener identifies on every profile snapshot, with fresh data, and the
            // write above brings one; identifying with this older snapshot would write its old counters back.
            trackFirstExperienceTestExposure(freeAllowanceAssignment, userStore.getState().currentUser ?? updatedUserObject, {
              identifyAfterwards: false,
              writeSideToAccount: false,
            });
          })
          .catch((error) => {
            console.log(error);
          });
      }
      if (
        updatedUserObject?.number_of_sets_of_cards_generated === undefined ||
        updatedUserObject?.number_of_sets_of_cards_generated === null ||
        (updatedUserObject?.number_of_sets_of_cards_generated === 1 &&
          !wasUserCreatedLessThanOneHourAgo(updatedUserObject))
      ) {
        if (!hasFetchedNumberOfSetsRef.current) {
          hasFetchedNumberOfSetsRef.current = true;
          const generationCountRevisionAtRead = generationCountRevision;
          // Historical count repair must not hold the authenticated home screen.
          void getNumberOfSetsOfCardsGeneratedForUser(userFirebaseAuth.uid)
            .then((numberOfSets) => {
              // Keep repairs across unrelated snapshots, but never overwrite a
              // count changed by generation or a replacement account session.
              if (
                !isSnapshotListenerActive ||
                auth.currentUser?.uid !== userFirebaseAuth.uid ||
                generationCountRevisionAtRead !== generationCountRevision
              ) return;
              if (numberOfSets > 0) {
                return updateUser(userFirebaseAuth.uid, { number_of_sets_of_cards_generated: numberOfSets });
              }
            })
            .catch((error) => {
              trackError(error, { scope: "user-generation-count-backfill", userId: userFirebaseAuth.uid });
            });
        }
      }
      if (
        !updatedUserObject?.user_settings?.notification_preferences // For users that not have notification preferences
      ) {
        updateUser(userFirebaseAuth?.uid, {
          user_settings: {
            ...updatedUserObject.user_settings,
            notification_preferences: {
              review_reminder_on_desktop: false, // Change to true on review-reminder-push-notification route
              review_reminder_on_mobile: true,
            },
          },
        });
      }

      // Check if should_rephrase_questions is undefined and set default value
      if (updatedUserObject?.user_settings && updatedUserObject.user_settings.should_rephrase_questions === undefined) {
        updatedUserObject.user_settings.should_rephrase_questions = true;
        updateUser(userFirebaseAuth?.uid, {
          user_settings: {
            ...updatedUserObject.user_settings,
            should_rephrase_questions: true,
          },
        });
      }

      if (
        !updatedUserObject?.is_page_generation_data_migrated_to_decks &&
        !updatedUserObject?.is_page_generation_data_migration_in_progress &&
        isFirebaseDate(updatedUserObject?.date_created) &&
        new Date(
          updatedUserObject?.date_created.seconds * 1000 + updatedUserObject?.date_created.nanoseconds / 1000000
        ) < new Date(2024, 6, 22) // Because new users do not have the page generation data
      ) {
        fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/user/migrate_page_generation_data_to_decks`, {
          method: "POST",
          body: JSON.stringify({ user_id: userFirebaseAuth.uid }),
          headers: {
            "Content-Type": "application/json",
          },
        }).then(async (data) => {
          // if (data.status === 200) {
          //   console.log("sended Page generation data migrated to decks");
          // }
        });
      }

      var webSessionId = localStorage.getItem("web_session_id");
      if (webSessionId) {
        fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/update_web_session`, {
          method: "POST",
          body: JSON.stringify({
            session_id: webSessionId,
            user_id: userFirebaseAuth.uid,
            is_logged_in: true,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then(async (data) => {
            if (data.status === 200) {
              localStorage.removeItem("web_session_id");
            }
          })
          .catch((error) => {
            // console.log(error);
          });
      }

      // migrate gamification properties if needed
      const newGamificationProperties = await migrateUserGamificationPropertiesIfNeeded(updatedUserObject);
      if (!isCurrentProfileSnapshot()) return;
      if (newGamificationProperties) {
        updatedUserObject.gamification_properties = newGamificationProperties;
      }

      // migrate badges progress and stats if needed
      const newBadgesProgressAndStats = await migrateUserBadgesProgressAndStatsIfNeeded(updatedUserObject);
      if (!isCurrentProfileSnapshot()) return;
      if (newBadgesProgressAndStats) {
        updatedUserObject.gamification_properties.badges_progress_and_stats = newBadgesProgressAndStats;
      }

      // migrate MegaMind users to Super Learner
      const wasMegaMindMigrated = await migrateMegaMindUsersToSuperLearner(updatedUserObject);
      if (!isCurrentProfileSnapshot()) return;
      if (wasMegaMindMigrated) {
        updatedUserObject.subscription_information.subscription_plan_type = UserSubscriptionPlan.SUPER_LEARNER;
        updatedUserObject.subscription_information.did_get_grandfathered_from_mega_mind_to_super_learner = true;
      }

      identifyUserForAnalytics(updatedUserObject);

      // ELU Analytics: attach the signed-in user's email to their session so product
      // analytics can attribute behavior to a real person instead of an anonymous
      // device. `identifyEluAnalyticsUser` waits for the async-loaded ELU SDK to be
      // ready (so returning users are still identified even if the script hasn't
      // finished downloading when this auth callback first fires) and never throws
      // (so an ELU failure can't strand the setCurrentUser/setAuthenticationLoading
      // calls below). See https://elu.dev for docs.
      const signedInUserEmail = updatedUserObject?.email_auth || userFirebaseAuth?.email;
      if (signedInUserEmail) {
        identifyEluAnalyticsUser(signedInUserEmail);
      }

      // Preserve existing counters until their normal monthly reset. New periods
      // use the existing allowance without waiting for a retired provider.
      const monthlyGenerationResetDate = getMonthlyGenerationResetDate(updatedUserObject);
      if (monthlyGenerationResetDate && monthlyGenerationResetDate.getTime() !== monthlyGenerationResetAttemptedForDateMs) {
        monthlyGenerationResetAttemptedForDateMs = monthlyGenerationResetDate.getTime();
        try {
          await checkAndUpdateUserGenerationLimits(updatedUserObject, NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED);
        } catch (error) {
          trackError(error, { scope: "user-generation-limit-maintenance", userId: userFirebaseAuth.uid });
        }
        if (!isCurrentProfileSnapshot()) return;
      }

      setCurrentUserInStore(updatedUserObject);
      setCurrentUser(updatedUserObject);
      hasLoadedAuthenticatedUserProfileRef.current = true;
      markStartupPhase("profile_ready");
      setAuthenticationLoading(isMobileAuthenticationBootstrapPending());

    }, (error) => {
      if (!isSnapshotListenerActive) return;
      // A terminal listener error must also invalidate any older async snapshot work.
      // Keep the signed-in session, but never render a stale account as usable.
      isSnapshotListenerActive = false;
      profileSnapshotRevision += 1;
      hasLoadedAuthenticatedUserProfileRef.current = false;
      setCurrentUserInStore(null);
      setCurrentUser(null);
      setAccountLoadError(true);
      setAuthenticationLoading(false);
      trackError(error, { scope: "user-profile-listener" });
      if (error.code === "permission-denied") {
        void signOutSessionStartedBeforeIncidentCutoff(userFirebaseAuth);
      }
    });
    return () => {
      isSnapshotListenerActive = false;
      snapshotUserDataUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [userFirebaseAuth]);

  const value: AuthContextProps = {
    currentUser,
    authenticationLoading,
    accountSetupError,
    accountLoadError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
